import type { Board, Member } from '@trello/model-types';
import { ProductFeatures } from '@trello/product-features';

import { canInviteMembers } from './canInviteMembers';
import { compareMemberTypes } from './compareMemberTypes';
import { getMemberTypeFromBoard } from './getMemberTypeFromBoard';
import { isAdminOfBoard } from './isAdminOfBoard';

/**
 * Checks if a member has permissions to remove members from the board.
 */
export const canRemoveMember = (
  me: Pick<
    Member,
    'id' | 'idPremOrgsAdmin' | 'idEnterprisesAdmin' | 'memberType'
  >,
  subject: Pick<
    Member,
    'id' | 'idPremOrgsAdmin' | 'idEnterprisesAdmin' | 'memberType'
  >,
  board: Pick<Board, 'memberships' | 'members' | 'organization'> & {
    prefs: Pick<NonNullable<Board['prefs']>, 'invitations' | 'canInvite'>;
  },
) => {
  const workspace = board.organization;
  // If there are no other explicit board admins, also check if this board is in a paid workspace and has a member who is a workspace admin
  const boardHasAdminOtherThanSubject =
    board.memberships.some(
      ({ idMember, memberType }) =>
        idMember !== subject.id && memberType === 'admin',
    ) ||
    (workspace &&
      ProductFeatures.hasProduct(workspace.products?.[0]) &&
      board.members?.some(
        (boardMember) =>
          boardMember.id &&
          boardMember.id !== subject.id &&
          isAdminOfBoard(boardMember as Member, board),
      ));

  const boardHasMoreThanOneMember = board.memberships.length > 1;
  const amAdmin = isAdminOfBoard(me, board);
  const amSubject = me.id === subject.id;

  if (boardHasMoreThanOneMember) {
    if (boardHasAdminOtherThanSubject && (amAdmin || amSubject)) {
      return true;
    }

    const meMemberType = getMemberTypeFromBoard(subject, board, false);
    if (
      !amSubject &&
      meMemberType !== 'org' &&
      canInviteMembers(me, board) &&
      compareMemberTypes(getMemberTypeFromBoard(me, board), meMemberType) >= 0
    ) {
      return true;
    }
  }
  return false;
};
